// Pure serialiser: BlockTree → Jinja2 string.
//
// The author works with a flat list of declarative blocks. The serialiser is
// responsible for:
//   1. Auto-grouping consecutive subject-bound blocks into a single
//      `{% for subject in subjects %}` loop. Subject-bound = the block
//      references `subject.X` in its content (or is a Section block).
//   2. Wrapping each section reference with a Jinja `or` fallback so that
//      empty sections render a "Nothing found for X" placeholder rather
//      than silently disappearing.
//
// Whitespace style mirrors the backend's default template:
//   `{%- ... -%}` around control flow, content separated by blank lines.

import { Block, BlockTree, FilterApplication, InlineNode } from '@/types';

export interface SerialiseContext {
  // Slug → display name lookup for the empty-section fallback message. Falls
  // back to a humanised slug if not provided.
  scopeComponentNamesBySlug?: Record<string, string>;
}

export function serialiseBlocks(tree: BlockTree, ctx: SerialiseContext = {}): string {
  // Walk the blocks, grouping consecutive subject-bound ones into a loop.
  const out: string[] = [];
  let i = 0;
  while (i < tree.blocks.length) {
    const b = tree.blocks[i];
    if (isSubjectBound(b)) {
      const run: Block[] = [];
      while (i < tree.blocks.length && isSubjectBound(tree.blocks[i])) {
        run.push(tree.blocks[i]);
        i++;
      }
      out.push(serialiseSubjectLoop(run, ctx));
    } else {
      out.push(serialiseBlock(b, ctx));
      i++;
    }
  }
  return `${out.join('').trimEnd()}\n`;
}

// ---------- subject-bound detection ----------

export function isSubjectBound(block: Block): boolean {
  switch (block.kind) {
    case 'section':
      return true;
    case 'variable':
      return block.path.startsWith('subject.') || block.path === 'subject';
    case 'heading':
    case 'paragraph':
      return inlineHasSubjectRef(block.content);
    case 'divider':
    case 'raw':
      return false;
  }
}

function inlineHasSubjectRef(nodes: InlineNode[]): boolean {
  return nodes.some(
    (n) => n.type === 'var' && (n.path.startsWith('subject.') || n.path === 'subject')
  );
}

// ---------- block emission ----------

function serialiseSubjectLoop(run: Block[], ctx: SerialiseContext): string {
  const body = run.map((b) => serialiseBlock(b, ctx)).join('');
  // No whitespace-control hyphens on the tags: `{%- ... -%}` would eat the
  // blank line separating the loop from a preceding block (e.g. a divider
  // followed by a section would render as `---## Heading`), and `{%- endfor %}`
  // would join consecutive subject iterations with no separator.
  return `{% for subject in subjects %}\n${body}{% endfor %}\n\n`;
}

function serialiseBlock(block: Block, ctx: SerialiseContext): string {
  switch (block.kind) {
    case 'heading': {
      const hashes = '#'.repeat(block.level);
      return `${hashes} ${serialiseInline(block.content)}\n\n`;
    }

    case 'paragraph':
      return `${serialiseInline(block.content)}\n\n`;

    case 'divider':
      return `---\n\n`;

    case 'section':
      return `${serialiseSection(block.slug, ctx)}\n\n`;

    case 'variable':
      return `${serialiseVariable(block.path, block.filters)}\n\n`;

    case 'raw':
      // Pass through verbatim; a trailing newline keeps neighbouring blocks
      // visually separated. trim/lstrip will collapse any duplicate.
      return `${block.source}\n`;
  }
}

function serialiseSection(slug: string, ctx: SerialiseContext): string {
  const name = displayNameForSlug(slug, ctx);
  const fallback = `## ${name}\n\n*Nothing found for ${name}.*`;
  const safeSlug = escapeJinjaSingleQuoted(slug);
  const safeFallback = escapeJinjaSingleQuoted(fallback);
  // Omit the section entirely when it doesn't apply to the subject's type
  // (slug absent from `subject.applicable_sections`). The `is not defined`
  // guard must come first so older render contexts / the preview path — which
  // don't set `applicable_sections` — still render every section (short-circuit
  // before the `in` test, which would otherwise raise on an undefined value).
  // An applicable-but-empty section still shows the "Nothing found" fallback.
  return (
    `{% if subject.applicable_sections is not defined or ` +
    `'${safeSlug}' in subject.applicable_sections %}` +
    `{{ subject.sections['${safeSlug}'] or '${safeFallback}' }}` +
    `{% endif %}`
  );
}

function displayNameForSlug(slug: string, ctx: SerialiseContext): string {
  const explicit = ctx.scopeComponentNamesBySlug?.[slug];
  if (explicit) {
    return explicit;
  }
  // Humanise the slug: hyphens / underscores → spaces, title-case words.
  return slug
    .replaceAll(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------- inline / variable / filter ----------

function serialiseInline(nodes: InlineNode[]): string {
  return nodes
    .map((n) => (n.type === 'text' ? n.value : serialiseVariable(n.path, n.filters)))
    .join('');
}

function serialiseVariable(path: string, filters: FilterApplication[]): string {
  const filterStr = filters.map(serialiseFilter).join('');
  return `{{ ${path}${filterStr} }}`;
}

function serialiseFilter(f: FilterApplication): string {
  if (f.args.length === 0) {
    return ` | ${f.name}`;
  }
  const args = f.args.map(quoteFilterArg).join(', ');
  return ` | ${f.name}(${args})`;
}

// Filter args are stored as plain strings; the serialiser wraps in single
// quotes (Jinja string literals). Escape any embedded single quotes.
function quoteFilterArg(arg: string): string {
  // Allow callers to pass an unquoted expression by prefixing with `=`
  // (used by the parser when round-tripping an existing call site). e.g.
  // `=42` → `42`, `=subject.id` → `subject.id`. Without the prefix we treat
  // the arg as a string literal.
  if (arg.startsWith('=')) {
    return arg.slice(1);
  }
  return `'${escapeJinjaSingleQuoted(arg)}'`;
}

function escapeJinjaSingleQuoted(s: string): string {
  return s
    .replaceAll('\\', String.raw`\\`)
    .replaceAll("'", String.raw`\'`)
    .replaceAll('\n', String.raw`\n`);
}
